// Plain (non-React) connectivity store — the single source of truth for
// "are we online." Exists as its own module (rather than living inside
// lib/network.jsx or lib/api.js) so both the axios interceptors (api.js, a
// plain module that runs outside React) and the NetworkProvider (a React
// component) can read/write it without a circular import between them.
// The health probe MUST resolve its host the same way lib/api.js does.
// Reading REACT_APP_BACKEND_URL raw here meant that in LAN dev, where the
// configured DHCP IP goes stale, the API calls succeeded (api.js rewrites
// the host) while this probe still hit the dead address — flipping the whole
// app to the offline screen with a perfectly reachable backend.
import { BACKEND_URL } from "./backendUrl";

const PROBE_TIMEOUT_MS = 5000;

// Interface-level "connected" (native Network plugin, browser navigator.onLine)
// can lie — e.g. wifi with no real internet — and so can a failed request
// (see api.js). Every connectivity verdict is therefore confirmed against the
// backend. A raw fetch (not the shared `api` axios instance) avoids recursing
// through its own interceptors.
export async function probeHealth() {
  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    const res = await fetch(`${BACKEND_URL}/api/health`, {
      signal: controller.signal,
      cache: "no-store",
    });
    clearTimeout(t);
    return res.ok;
  } catch {
    return false;
  }
}

let online = true;

const listeners = new Set();

export function getOnline() {
  return online;
}

export function setOnline(v) {
  if (v !== online) {
    online = v;
    listeners.forEach((l) => l(online));
  }
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
